import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, message, Modal, Spin } from 'antd';
import {
  ArrowLeftOutlined,
  QrcodeOutlined,
  CameraOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { LuBarcode } from "react-icons/lu";
import {
  EVENTS_URL,
  EVENT_SUBMISSIONS_SCAN_URL,
  EVENT_SUBMISSIONS_CHECKIN_URL,
} from '../../api/URLs';
import { getDataRequest, putDataRequest } from '../../api/serviceMethods';
import { normalizeSubmission } from './EventAdmissionPage';
import SearchSvg from '../../assets/images/SearchSvg';
import HeaderComponent from '../../components/header/headerComponent';
import '../../styles/event.css';
import {
  CameraSvg,
  InfoSvg,
  KeyboardSvg,
  ScanBarCodeSvg,
  ScanFailSvg,
  ScanIpadEyeSvg,
  ScanIpadSvg,
  ScanModalSvg,
} from '../../assets/images/HeaderSvg';

const DEFAULT_POSTER =
  'https://fastly.picsum.photos/id/30/1280/901.jpg?hmac=A_hpFyEavMBB7Dsmmp53kPXKmatwM05MUDatlWSgATE';

const formatDatePart = (isoStr) => {
  if (!isoStr) return '';
  try {
    const dt = new Date(isoStr);
    if (isNaN(dt.getTime())) return isoStr;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}.${m}.${d}`;
  } catch {
    return isoStr;
  }
};

const EventScanPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const navEvent = location.state?.event;
  const [eventDetail, setEventDetail] = useState(navEvent || null);
  const [eventLoading, setEventLoading] = useState(false);

  // Inputs state
  const [barcodeValue, setBarcodeValue] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Real camera & scanner state
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  // Verification Modals State matching design
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [notFoundModalVisible, setNotFoundModalVisible] = useState(false);
  const [alreadyUsedModalVisible, setAlreadyUsedModalVisible] = useState(false);
  const [notFoundMessage, setNotFoundMessage] = useState('');
  const [alreadyUsedMessage, setAlreadyUsedMessage] = useState('');
  const [matchedAttendee, setMatchedAttendee] = useState(null);

  const barcodeInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const isScanningRef = useRef(false);
  const anyModalOpenRef = useRef(false);
  anyModalOpenRef.current =
    resultModalVisible || notFoundModalVisible || alreadyUsedModalVisible || verifying;

  // Fetch Event Detail from live API
  const fetchEventDetail = useCallback(async () => {
    const targetId =
      id ||
      location.state?.event?.eventId ||
      location.state?.event?._id ||
      location.state?.event?.id;
    if (!targetId) return;
    try {
      setEventLoading(true);
      const res = await getDataRequest(`${EVENTS_URL}/${targetId}`);
      setEventLoading(false);
      if (res && (res.status === 200 || res.data)) {
        const data = res.data || res;
        setEventDetail(data);
      }
    } catch (err) {
      setEventLoading(false);
      console.error('Error fetching event detail:', err);
    }
  }, [id, location.state?.event]);

  useEffect(() => {
    fetchEventDetail();
  }, [fetchEventDetail]);

  const effectiveEventId = eventDetail?._id || id || eventDetail?.eventId;

  // Code Lookup via live API:
  // GET /admin/events/submitions-scan/:id?eventId=${eventId}
  // 1. Matched and waiting (Status 200) -> message.success(res.message) & Modal 1: 扫描结果 (Result Modal)
  // 2. Not matched (Status 404) -> Modal 2: 无法匹配到任何受邀人员 (Shows API message: "Unable to find any invitees")
  // 3. Matched but already admitted (Status 400) -> Modal 3: (Shows API message: "The QR code has already been used, and the matched invitees have already entered the venue.")
  const handleVerifyCode = async (codeToSearch) => {
    if (!codeToSearch || !codeToSearch.trim()) {
      message.warning(t('Please enter or scan admission code'));
      return;
    }

    if (verifying) return;

    const cleanCode = codeToSearch.trim();
    const queryEventId = eventDetail?._id || id || eventDetail?.eventId;

    if (!queryEventId) {
      message.error(t('Event information missing'));
      return;
    }

    try {
      setVerifying(true);
      const res = await getDataRequest(
        `${EVENT_SUBMISSIONS_SCAN_URL}/${encodeURIComponent(cleanCode)}`,
        { eventId: queryEventId }
      );
      setVerifying(false);
      setBarcodeValue('');
      setManualCode('');

      if (res && res.status === 200 && res.data) {
        const submissionRaw = res.data;
        const normalized = normalizeSubmission(submissionRaw, i18n.language) || submissionRaw;

        // If already checked in
        if (
          submissionRaw.isCheckedIn ||
          submissionRaw.isAlreadyCheckedIn ||
          res.isAlreadyCheckedIn
        ) {
          setMatchedAttendee(normalized);
          setAlreadyUsedMessage(
            t(res.message) ||
            t('The QR code has already been used, and the matched invitees have already entered the venue.') ||
            '该二维码已被使用过，匹配到的受邀人员已入场'
          );
          setAlreadyUsedModalVisible(true);
          return;
        }

        if (res.message) {
          message.success(t(res.message));
        }
        setMatchedAttendee(normalized);
        setResultModalVisible(true);
      } else if (res && (res.status === 400 || res.isAlreadyCheckedIn)) {
        if (res.data) {
          setMatchedAttendee(normalizeSubmission(res.data, i18n.language));
        }
        setAlreadyUsedMessage(
          t(res.message) ||
          t('The QR code has already been used, and the matched invitees have already entered the venue.') ||
          '该二维码已被使用过，匹配到的受邀人员已入场'
        );
        setAlreadyUsedModalVisible(true);
      } else {
        setNotFoundMessage(t
          (res?.message) ||
          t('Unable to find any invitees') ||
          '无法匹配到任何受邀人员'
        );
        setNotFoundModalVisible(true);
      }
    } catch (err) {
      setVerifying(false);
      setBarcodeValue('');
      setManualCode('');
      console.error('Scan code error:', err);
      const errorObj = err?.response?.data || err || {};
      const apiMsg = errorObj?.message || (typeof err === 'string' ? err : '');
      const isAlready =
        errorObj?.status === 400 ||
        errorObj?.isAlreadyCheckedIn ||
        errorObj?.data?.isCheckedIn ||
        (apiMsg &&
          (apiMsg.toLowerCase().includes('already') ||
            apiMsg.includes('已使用') ||
            apiMsg.includes('已入场')));

      if (isAlready) {
        if (errorObj?.data) {
          setMatchedAttendee(normalizeSubmission(errorObj.data, i18n.language));
        }
        setAlreadyUsedMessage(
          t(apiMsg) ||
          t('The QR code has already been used, and the matched invitees have already entered the venue.') ||
          '该二维码已被使用过，匹配到的受邀人员已入场'
        );
        setAlreadyUsedModalVisible(true);
      } else {
        // 404 or other not found
        setNotFoundMessage(
          t(apiMsg) ||
          t('Unable to find any invitees') ||
          '无法匹配到任何受邀人员'
        );
        setNotFoundModalVisible(true);
      }
    }
  };

  // Confirm Admission via live API:
  // PUT /admin/events/submitions-checkin/:id?eventId=${eventId}
  const handleConfirmAdmission = async () => {
    if (!matchedAttendee) return;
    const submissionId = matchedAttendee._id || matchedAttendee.id;
    const queryEventId = eventDetail?._id || id || eventDetail?.eventId;

    if (!submissionId) {
      message.error(t('Invalid attendee information'));
      return;
    }

    try {
      setConfirming(true);
      const url = `${EVENT_SUBMISSIONS_CHECKIN_URL}/${encodeURIComponent(
        submissionId
      )}?eventId=${encodeURIComponent(queryEventId)}`;
      const res = await putDataRequest(url, {});
      setConfirming(false);

      if (res && (res.status === 200 || res.status === 204 || res.success || res.message)) {
        message.success(t(res.message));
        setResultModalVisible(false);
        setMatchedAttendee(null);
        setBarcodeValue('');
        setManualCode('');
        // Re-fetch event details to update admitted count banner in real time
        fetchEventDetail();
      } else {
        message.error(res?.message || t('Admission checkin failed'));
      }
    } catch (err) {
      setConfirming(false);
      console.error('Confirm checkin error:', err);
      const errorObj = err?.response?.data || err || {};
      const errMsg = errorObj?.message || (typeof err === 'string' ? err : '') || t('Admission checkin failed');
      message.error(errMsg);
    }
  };

  // Stop camera stream & scan timer
  const stopCamera = () => {
    if (scanTimerRef.current) {
      clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setCameraLoading(false);
  };

  // Start continuous QR / barcode detection from the video stream
  const startScanning = () => {
    if (scanTimerRef.current) clearInterval(scanTimerRef.current);

    const hasBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

    scanTimerRef.current = setInterval(async () => {
      if (isScanningRef.current || anyModalOpenRef.current) return;
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;

      if (hasBarcodeDetector) {
        try {
          isScanningRef.current = true;
          const detector = new window.BarcodeDetector({
            formats: ['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8'],
          });
          const detected = await detector.detect(video);
          if (detected && detected.length > 0 && detected[0].rawValue) {
            handleVerifyCode(detected[0].rawValue);
          }
        } catch (e) {
          // ignore detection skip
        } finally {
          isScanningRef.current = false;
        }
      } else if (window.jsQR && video.videoWidth > 0 && video.videoHeight > 0) {
        try {
          isScanningRef.current = true;
          const canvas = document.createElement('canvas');
          canvas.width = Math.min(video.videoWidth, 480);
          canvas.height = Math.min(video.videoHeight, 360);
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = window.jsQR(imgData.data, imgData.width, imgData.height);
            if (code && code.data) {
              handleVerifyCode(code.data);
            }
          }
        } catch (e) {
        } finally {
          isScanningRef.current = false;
        }
      }
    }, 400);
  };

  // Start camera via getUserMedia
  const startCamera = async () => {
    setCameraLoading(true);
    setCameraError(null);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const errTxt =
        t('Camera not supported notice') ||
        '当前浏览器环境无法访问物理摄像头（通常需要 HTTPS 或 localhost 访问）。您仍可点击下方按钮模拟扫码体验。';
      setCameraError(errTxt);
      setCameraActive(true);
      setCameraLoading(false);
      message.warning(errTxt);
      return;
    }

    try {
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setCameraActive(true);
      setCameraLoading(false);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch((err) => console.log('Video play error:', err));
      }

      startScanning();
    } catch (err) {
      console.error('Camera stream error:', err);
      let errMsg =
        t('Camera permission denied or not found') ||
        '无法访问摄像头，请确认已授予浏览器摄像头权限。';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errMsg =
          t('Camera permission denied') ||
          '浏览器拒绝了摄像头权限，请在地址栏设置中允许摄像头访问后重试。';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errMsg = t('No camera device found');
      }
      setCameraError(errMsg);
      setCameraActive(true);
      setCameraLoading(false);
      message.error(errMsg);
    }
  };

  // Ensure stream attaches when videoRef becomes available
  useEffect(() => {
    if (cameraActive && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => { });
    }
  }, [cameraActive]);

  // Load jsQR script fallback if native BarcodeDetector is absent
  useEffect(() => {
    if (typeof window !== 'undefined' && !('BarcodeDetector' in window) && !window.jsQR) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js';
      script.async = true;
      document.body.appendChild(script);
    }
    return () => {
      stopCamera();
    };
  }, []);

  // Formatted Top Banner properties
  const eventTitle =
    eventDetail?.localize?.[i18n.language]?.title ||
    eventDetail?.localize?.cn?.title ||
    eventDetail?.localize?.en?.title ||
    eventDetail?.title ||
    '-';
  const eventPoster =
    eventDetail?.images?.[0]?.url ||
    eventDetail?.poster ||
    DEFAULT_POSTER;
  const eventTime =
    eventDetail?.startDate && eventDetail?.endDate
      ? `${formatDatePart(eventDetail.startDate)}-${formatDatePart(eventDetail.endDate)}`
      : eventDetail?.time || '-';
  const eventCreator =
    eventDetail?.createdBy?.name ||
    eventDetail?.creator ||
    'ESI System';
  const admittedCount = eventDetail?.checkedCount ?? eventDetail?.checkedCount ?? 0;
  const totalQuota = eventDetail?.registeredCount ?? eventDetail?.registeredCount ?? 0;
  const isExpired = Boolean(
    location.state?.tab === 'expired' ||
    location.state?.event?.isExpired ||
    eventDetail?.isExpired ||
    eventDetail?.status === 'expired' ||
    (eventDetail?.endDate && new Date(eventDetail.endDate).getTime() < new Date().setHours(0, 0, 0, 0))
  );

  return (
    <HeaderComponent
      isHide="view"
      pageTitle={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            onClick={() =>
              navigate(`/events/${effectiveEventId || id}/admission`, {
                state: { event: eventDetail },
              })
            }
          >
            <ArrowLeftOutlined />
            <span style={{fontSize: 14, color: '#6B7280', fontWeight: 'normal'}}>{t('Back to Entry List')}</span>
          </span>
          <span style={{ color: '#D1D5DB', margin: '0 6px', fontWeight: 'normal' }}>|</span>
          <span
            style={{ cursor: 'pointer', fontSize: 14, color: '#000000', fontWeight: 500 }}
            onClick={() =>
              navigate(`/events/${effectiveEventId || id}/admission`, {
                state: { event: eventDetail },
              })
            }
          >
            {t('Scan Admission')}
          </span>
          <span style={{ color: '#D1D5DB', margin: '0 6px', fontWeight: 'normal' }}>|</span>
          <span style={{ fontSize: 14, color: '#6B7280', fontWeight: 'normal', cursor: 'default' }}>
            {t('Scan The QR Code to Enter')}
          </span>
        </span>
      }
    >
      <div className="box-shadow h-100 w-100 event-page-wrapper">
        <div className="w-100 h-100 overflow-auto" style={{ background: '#fafbfc' }}>
          <div className="event-subpage-container">
            {/* Top Live Event Banner Card */}
            <Spin spinning={eventLoading}>
              <div className="scan-page-banner">
                <div className="left-info">
                  <img
                    src={eventPoster}
                    alt={eventTitle}
                    className="thumb"
                  />
                  <div>
                    <div className="title-row">
                      <h3 className="title">{eventTitle}</h3>
                      <span className={`event-status-tag ${isExpired ? 'expired' : 'active'}`}>
                        {isExpired ? t('Expired') : t('Ongoing')}
                      </span>
                    </div>
                    <div className="meta">
                      <span>{t('Event Time')}: {eventTime}</span>
                      <span>{t('Creator')}: {eventCreator}</span>
                    </div>
                  </div>
                </div>

                <div className="right-stat">
                  <div className="stat-label">{t('Admitted Count')}</div>
                  <div className="stat-val">
                    {admittedCount}
                    <span className="total">/{totalQuota}</span>
                  </div>
                </div>
              </div>
            </Spin>

            {/* Section Title matching Mockup */}
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}
            >
              <h2 style={{ fontSize: 18, fontWeight: 600, color: '#1F2937', margin: 0 }}>
                {t('Scan Admission')}
              </h2>
              <span style={{ fontSize: 13, color: '#6B7280' }}>
                {t('Scan or enter admission code, match attendee then confirm admission')}
              </span>
            </div>

            {/* 2-Columns Scanner Area */}
            <div className="scan-cards-grid">
              {/* Left Column: iPad Camera Scanner */}
              <div className="scan-box-card">
                <div className="scan-box-header">
                  <h4 className="scan-box-title">
                    <CameraSvg />
                    {t('iPad Camera Scanner')}
                  </h4>
                  <span
                    className={
                      cameraActive && !cameraError ? 'scan-tag-active' : 'scan-tag-waiting'
                    }
                  >
                    {cameraActive && !cameraError ? t('Scanning') : t('Waiting for Scan')}
                  </span>
                </div>

                <div
                  className={`camera-viewfinder-box ${cameraActive && !cameraError ? 'is-active' : ''
                    }`}
                >
                  {!cameraActive ? (
                    <div className="camera-scan-frame">
                      <div className="camera-scan-brackets">
                        <ScanIpadSvg />
                      </div>
                      <div style={{ textAlign: 'center', padding: '0 24px', zIndex: 2 }}>
                        <div style={{ marginBottom: 12 }}>
                          <ScanIpadEyeSvg />
                        </div>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: '#262626',
                            marginBottom: 4,
                          }}
                        >
                          {t('Waiting to Open Camera')}
                        </div>
                        <div style={{ fontSize: 13, color: '#636E88', marginBottom: 20 }}>
                          {t('Align Code in Frame')}
                        </div>
                        <Button
                          type="primary"
                          loading={cameraLoading}
                          style={{
                            backgroundColor: '#E50A47',
                            borderColor: '#E50A47',
                            borderRadius: 6,
                            height: 38,
                            padding: '0 24px',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                          onClick={startCamera}
                        >
                          <CameraOutlined />
                          {t('Open Camera')}
                        </Button>
                      </div>
                    </div>
                  ) : cameraError ? (
                    <div
                      className="camera-scan-frame"
                      style={{ width: '90%', maxWidth: 360, textAlign: 'center' }}
                    >
                      <div style={{ marginBottom: 12 }}>
                        <ExclamationCircleOutlined style={{ fontSize: 40, color: '#f59e0b' }} />
                      </div>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: '#262626',
                          marginBottom: 6,
                        }}
                      >
                        {t('Camera Start Failed')}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: '#636E88',
                          marginBottom: 20,
                          lineHeight: 1.5,
                        }}
                      >
                        {cameraError}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 10,
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Button
                          style={{
                            backgroundColor: '#E50A47',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            height: 34,
                            fontSize: 13,
                          }}
                          onClick={startCamera}
                        >
                          {t('Retry Camera')}
                        </Button>
                        <Button
                          style={{ borderRadius: 6, height: 34, fontSize: 13 }}
                          onClick={stopCamera}
                        >
                          {t('Turn Off Camera')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Live Video Feed */}
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="camera-video-element"
                      />

                      {/* Dark Vignette Overlay */}
                      <div className="camera-dark-overlay" />

                      {/* Centered Scanning Target Frame */}
                      <div className="camera-scan-frame">
                        <div className="camera-scan-brackets">
                          <ScanIpadSvg />
                        </div>
                        {/* Animated Laser Scan Beam */}
                        <div className="camera-laser-line" />

                        {/* Subtle Watermark behind laser */}
                        <QrcodeOutlined
                          style={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.15)' }}
                        />
                      </div>

                      {/* Floating Bottom Toolbar */}
                      <div className="camera-active-toolbar">
                        <div className="camera-status-indicator">
                          <span className="camera-pulse-dot" />
                          <span>{t('Camera is running, focusing and recognizing...')}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <Button
                            size="small"
                            ghost
                            style={{
                              borderColor: 'rgba(255, 255, 255, 0.35)',
                              color: '#ffffff',
                              borderRadius: 4,
                              height: 28,
                              fontSize: 12,
                            }}
                            onClick={stopCamera}
                          >
                            {t('Turn Off Camera')}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 16,
                    fontSize: 12,
                    color: '#636E88',
                    display: 'flex',
                    gap: 6,
                  }}
                >
                  <InfoSvg />
                  <span>{t('Camera Notice')}</span>
                </div>
              </div>

              {/* Right Column: External Scanner & Manual Input */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Top: External Barcode Scanner */}
                <div className="scan-box-card">
                  <div className="scan-box-header">
                    <h4 className="scan-box-title">
                      <ScanBarCodeSvg />
                      {t('External Barcode Scanner')}
                    </h4>
                    <span className="scan-tag-waiting">{t('Waiting for Barcode Input')}</span>
                  </div>

                  <p style={{ fontSize: 13, color: '#636E88', margin: '0 0 14px 0' }}>
                    {t('Connect Scanner Notice')}
                  </p>

                  <div className="barcode-input-container">
                    <LuBarcode style={{ fontSize: 22, color: '#636E88' }} />
                    <input
                      ref={barcodeInputRef}
                      placeholder={t('Click to receive barcode')}
                      value={barcodeValue}
                      disabled={verifying}
                      onChange={(e) => setBarcodeValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleVerifyCode(barcodeValue);
                        }
                      }}
                    />
                  </div>

                  <p style={{ fontSize: 12, color: '#636E88', margin: '14px 0 0 0' }}>
                    {t('Scanner Enter Hint')}
                  </p>
                </div>

                {/* Bottom: Manual Input */}
                <div className="scan-box-card">
                  <div className="scan-box-header">
                    <h4 className="scan-box-title">
                      <KeyboardSvg /> {t('Manual Input')}
                    </h4>
                  </div>

                  <div style={{ fontSize: 13, color: '#636E88', marginBottom: 8 }}>
                    {t('Admission / Invitation Code')}
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <Input
                      placeholder={t('Enter Admission Code Placeholder')}
                      value={manualCode}
                      disabled={verifying}
                      onChange={(e) => setManualCode(e.target.value)}
                      onPressEnter={() => handleVerifyCode(manualCode)}
                      style={{ height: 38, borderRadius: 6 }}
                    />
                    <Button
                      loading={verifying}
                      style={{
                        backgroundColor: '#E50A47',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        height: 38,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                      onClick={() => handleVerifyCode(manualCode)}
                    >
                      <SearchSvg color="#FFFFFF" opacity="1" /> {t('Query & Verify')}
                    </Button>
                  </div>

                  <p style={{ fontSize: 12, color: '#636E88', margin: '14px 0 0 0' }}>
                    {t('Manual Input Notice')}
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Verification Process Flow Bar */}
            <div className="scan-flow-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span>{t('Admission Verification Flow')}</span>
                <div className="scan-flow-steps">
                  <span>{t('Scan / Enter Code')}</span>
                  <span className="step-arrow">→</span>
                  <span>{t('Verify Attendee Info')}</span>
                  <span className="step-arrow">→</span>
                  <span>{t('Confirm Admission Step')}</span>
                </div>
              </div>
              <div>{t('Scan Failure Notice')}</div>
            </div>
          </div>
        </div>

        {/* Modal 1: 扫描结果 (Scan Result Modal - Matching Red Box) */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <ScanModalSvg />
              <span style={{ fontSize: 16, fontWeight: 600, color: '#1F2937' }}>
                {t('Scan Result')}
              </span>
            </div>
          }
          open={resultModalVisible}
          onCancel={() => {
            setResultModalVisible(false);
            setMatchedAttendee(null);
            setBarcodeValue('');
            setManualCode('');
          }}
          footer={null}
          width={520}
          centered
          destroyOnClose
        >
          {matchedAttendee && (
            <div style={{ padding: '8px 0 4px' }}>
              <div
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  padding: '24px 28px',
                  background: '#ffffff',
                  marginBottom: 24,
                }}
              >
                <div style={{ display: 'flex', marginBottom: 14, fontSize: 14 }}>
                  <span style={{ width: 125, color: '#4B5563' }}>
                    {t('Applicant Name')}：
                  </span>
                  <span style={{ color: '#1F2937', fontWeight: 500 }}>
                    {matchedAttendee.name}
                  </span>
                </div>
                <div style={{ display: 'flex', marginBottom: 14, fontSize: 14 }}>
                  <span style={{ width: 125, color: '#4B5563' }}>
                    {t('Phone Number')}：
                  </span>
                  <span style={{ color: '#1F2937', fontWeight: 500 }}>
                    {matchedAttendee.phone}
                  </span>
                </div>
                <div style={{ display: 'flex', marginBottom: 14, fontSize: 14 }}>
                  <span style={{ width: 125, color: '#4B5563' }}>
                    {t('Salon / Company')}：
                  </span>
                  <span style={{ color: '#1F2937', fontWeight: 500 }}>
                    {matchedAttendee.company}
                  </span>
                </div>
                <div style={{ display: 'flex', marginBottom: 14, fontSize: 14 }}>
                  <span style={{ width: 125, color: '#4B5563' }}>
                    {t('Invitation Code')}：
                  </span>
                  <span style={{ color: '#1F2937', fontWeight: 500 }}>
                    {matchedAttendee.localize?.cn?.code || matchedAttendee.code || matchedAttendee.ticketCode || '-'}
                  </span>
                </div>
                <div style={{ display: 'flex', marginBottom: 14, fontSize: 14 }}>
                  <span style={{ width: 125, color: '#4B5563' }}>
                    {t('Email')}：
                  </span>
                  <span style={{ color: '#1F2937', fontWeight: 500 }}>
                    {matchedAttendee.email || '-'}
                  </span>
                </div>
                <div style={{ display: 'flex', fontSize: 14 }}>
                  <span style={{ width: 125, color: '#4B5563' }}>
                    {t('Registration Time')}：
                  </span>
                  <span style={{ color: '#1F2937', fontWeight: 500 }}>
                    {matchedAttendee.regTime || '-'}
                  </span>
                </div>
              </div>

              {/* Yellow Box: Confirm Admission Button */}
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <Button
                  loading={confirming}
                  disabled={confirming}
                  style={{
                    backgroundColor: '#E50A47',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 6,
                    height: 42,
                    width: 220,
                    fontSize: 15,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                  onClick={handleConfirmAdmission}
                >
                  {t('Confirm Admission')}
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Modal 2: 无法匹配到任何受邀人员 (Not Found Modal - Image 2) */}
        <Modal
          open={notFoundModalVisible}
          onCancel={() => {
            setNotFoundModalVisible(false);
            setNotFoundMessage('');
            setBarcodeValue('');
            setManualCode('');
          }}
          footer={null}
          centered
          width={480}
          destroyOnClose
          bodyStyle={{ padding: '44px 28px 48px', textAlign: 'center' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ScanFailSvg />
            <div
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: '#1F2937',
                letterSpacing: '0.2px',
                marginTop: 12,
              }}
            >
              {t(notFoundMessage) || t('Unable to find any invitees')}
            </div>
          </div>
        </Modal>

        {/* Modal 3: 该二维码已被使用过，匹配到的受邀人员已入场 (Already Admitted Modal - Image 1) */}
        <Modal
          open={alreadyUsedModalVisible}
          onCancel={() => {
            setAlreadyUsedModalVisible(false);
            setMatchedAttendee(null);
            setAlreadyUsedMessage('');
            setBarcodeValue('');
            setManualCode('');
          }}
          footer={null}
          centered
          width={520}
          destroyOnClose
          bodyStyle={{ padding: '44px 28px 48px', textAlign: 'center' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <ScanFailSvg />
            <div
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: '#1F2937',
                letterSpacing: '0.2px',
                lineHeight: 1.4,
                marginTop: 12,
              }}
            >
              {t(alreadyUsedMessage) ||
                t(
                  'The QR code has already been used, and the matched invitees have already entered the venue.'
                )}
            </div>
          </div>
        </Modal>
      </div>
    </HeaderComponent>
  );
};

export default EventScanPage;
